<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import UserAccessLogsAPI from 'dashboard/api/userAccessLogs';
import Select from 'dashboard/components-next/select/Select.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();

const isLoading = ref(false);
const isLoadingSummary = ref(false);
const activeTab = ref('summary');
const selectedUserId = ref('');
const startDate = ref('');
const endDate = ref('');
const summaries = ref([]);
const logs = ref([]);
const logsMeta = ref({ total: 0, page: 1, per_page: 50 });

// Quick period presets
const period = ref('30');
const periodOptions = [
  { value: '7', label: 'Últimos 7 dias' },
  { value: '15', label: 'Últimos 15 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '60', label: 'Últimos 60 dias' },
  { value: '90', label: 'Últimos 90 dias' },
  { value: 'custom', label: 'Personalizado' },
];

const agents = computed(() => store.getters['agents/getAgents'] || []);
const agentOptions = computed(() => {
  const opts = [{ value: '', label: 'Todos os agentes' }];
  agents.value.forEach(a => {
    opts.push({ value: a.id, label: a.available_name || a.name });
  });
  return opts;
});

function applyPeriod() {
  if (period.value !== 'custom') {
    const days = parseInt(period.value, 10);
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    startDate.value = start.toISOString().slice(0, 10);
    endDate.value = end.toISOString().slice(0, 10);
  }
}

function formatDateTime(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}min`;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

const EVENT_MAP = {
  sign_in: { label: 'Entrada', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: 'i-lucide-log-in' },
  sign_out: { label: 'Saída', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: 'i-lucide-log-out' },
  conversation_opened: { label: 'Abriu Conversa', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: 'i-lucide-folder-open' },
  conversation_resolved: { label: 'Resolveu Conversa', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: 'i-lucide-check-circle' },
};

function eventLabel(type) {
  return EVENT_MAP[type]?.label || type;
}

function eventColor(type) {
  return EVENT_MAP[type]?.color || 'bg-n-slate-3 text-n-slate-11';
}

function eventIcon(type) {
  return EVENT_MAP[type]?.icon || 'i-lucide-activity';
}

async function loadSummary() {
  isLoadingSummary.value = true;
  try {
    const params = {};
    if (startDate.value) params.start = startDate.value;
    if (endDate.value) params.end = endDate.value;
    if (selectedUserId.value) params.user_id = selectedUserId.value;
    const { data } = await UserAccessLogsAPI.summary(params);
    summaries.value = data.summaries || [];
  } catch { summaries.value = []; }
  isLoadingSummary.value = false;
}

async function loadLogs(page = 1) {
  isLoading.value = true;
  try {
    const params = { page };
    if (startDate.value) params.start = startDate.value;
    if (endDate.value) params.end = endDate.value;
    if (selectedUserId.value) params.user_id = selectedUserId.value;
    const { data } = await UserAccessLogsAPI.get(params);
    logs.value = data.access_logs || [];
    logsMeta.value = data.meta || { total: 0, page: 1, per_page: 50 };
  } catch { logs.value = []; }
  isLoading.value = false;
}

function loadData() {
  if (activeTab.value === 'summary') loadSummary();
  else loadLogs(1);
}

function nextPage() {
  const { page, per_page, total } = logsMeta.value;
  if (page * per_page < total) loadLogs(page + 1);
}
function prevPage() {
  if (logsMeta.value.page > 1) loadLogs(logsMeta.value.page - 1);
}

watch(period, () => {
  applyPeriod();
  loadData();
});
watch(selectedUserId, () => loadData());
watch(activeTab, () => loadData());

onMounted(async () => {
  await store.dispatch('agents/get');
  applyPeriod();
  loadData();
});
</script>

<template>
  <div class="flex flex-col flex-1 overflow-auto p-6">
    <div class="mb-6">
      <h1 class="text-xl font-semibold text-n-slate-12">Registro de Atividade</h1>
      <p class="text-sm text-n-slate-11 mt-1">Acompanhe acessos e atividades dos agentes na plataforma.</p>
    </div>

    <div class="flex flex-wrap items-end gap-4 mb-6">
      <div>
        <label class="mb-1.5 text-heading-3 text-n-slate-12 block">Período</label>
        <Select v-model="period" :options="periodOptions" />
      </div>
      <template v-if="period === 'custom'">
        <NextInput v-model="startDate" type="date" label="De" />
        <NextInput v-model="endDate" type="date" label="Até" />
        <NextButton label="Filtrar" icon="i-lucide-search" @click="loadData" />
      </template>
      <div>
        <label class="mb-1.5 text-heading-3 text-n-slate-12 block">Agente</label>
        <Select v-model="selectedUserId" :options="agentOptions" />
      </div>
    </div>

    <div class="flex gap-1 mb-6 border-b border-n-weak">
      <button
        class="px-4 py-2.5 text-sm font-medium transition-colors relative"
        :class="activeTab === 'summary' ? 'text-n-brand' : 'text-n-slate-11 hover:text-n-slate-12'"
        @click="activeTab = 'summary'"
      >
        <span class="flex items-center gap-1.5">
          <span class="i-lucide-bar-chart-3 size-4" />
          Resumo
        </span>
        <span v-if="activeTab === 'summary'" class="absolute bottom-0 left-0 right-0 h-0.5 bg-n-brand rounded-full" />
      </button>
      <button
        class="px-4 py-2.5 text-sm font-medium transition-colors relative"
        :class="activeTab === 'logs' ? 'text-n-brand' : 'text-n-slate-11 hover:text-n-slate-12'"
        @click="activeTab = 'logs'"
      >
        <span class="flex items-center gap-1.5">
          <span class="i-lucide-list size-4" />
          Registro Detalhado
        </span>
        <span v-if="activeTab === 'logs'" class="absolute bottom-0 left-0 right-0 h-0.5 bg-n-brand rounded-full" />
      </button>
    </div>

    <div v-if="isLoading || isLoadingSummary" class="flex items-center justify-center py-20">
      <span class="i-lucide-loader-2 animate-spin size-7 text-n-brand" />
    </div>

    <template v-else-if="activeTab === 'summary'">
      <div v-if="summaries.length === 0" class="flex flex-col items-center justify-center py-20 text-center">
        <span class="i-lucide-user-search size-16 text-n-slate-8 mb-4" />
        <h3 class="text-lg font-semibold text-n-slate-12 mb-1">Nenhum dado encontrado</h3>
        <p class="text-sm text-n-slate-11">Selecione outro período ou agente.</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-weak">
              <th class="text-left py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Agente</th>
              <th class="text-center py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Entradas</th>
              <th class="text-center py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Saídas</th>
              <th class="text-center py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Primeiro Acesso</th>
              <th class="text-center py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Último Acesso</th>
              <th class="text-center py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">
                <span class="flex items-center justify-center gap-1">
                  <span class="i-lucide-folder-open size-3.5 text-green-500" />
                  Abertas
                </span>
              </th>
              <th class="text-center py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">
                <span class="flex items-center justify-center gap-1">
                  <span class="i-lucide-check-circle size-3.5 text-blue-500" />
                  Resolvidas
                </span>
              </th>
              <th class="text-center py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Total Ações</th>
              <th class="text-center py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">
                <span class="flex items-center justify-center gap-1">
                  <span class="i-lucide-clock size-3.5 text-green-500" />
                  Média Online/dia
                </span>
              </th>
              <th class="text-center py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">
                <span class="flex items-center justify-center gap-1">
                  <span class="i-lucide-clock size-3.5 text-red-400" />
                  Média Offline/dia
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in summaries" :key="s.user_id" class="border-b border-n-weak/50 hover:bg-n-alpha-1 transition-colors">
              <td class="py-3 px-3">
                <div class="flex items-center gap-2.5">
                  <div class="size-8 rounded-full bg-n-brand/10 text-n-brand flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {{ (s.user_name || '?')[0].toUpperCase() }}
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-n-slate-12 truncate">{{ s.user_name }}</p>
                    <p class="text-[10px] text-n-slate-10 truncate">{{ s.user_email }}</p>
                  </div>
                  <span
                    class="size-2 rounded-full flex-shrink-0"
                    :class="{
                      'bg-green-500': s.availability === 'online',
                      'bg-yellow-500': s.availability === 'busy',
                      'bg-n-slate-7': s.availability === 'offline',
                    }"
                  />
                </div>
              </td>
              <td class="py-3 px-3 text-center font-medium text-n-slate-12">{{ s.sign_in_count }}</td>
              <td class="py-3 px-3 text-center font-medium text-n-slate-12">{{ s.sign_out_count }}</td>
              <td class="py-3 px-3 text-center text-xs text-n-slate-11">{{ formatDateTime(s.first_sign_in) }}</td>
              <td class="py-3 px-3 text-center text-xs text-n-slate-11">{{ formatDateTime(s.last_sign_in) }}</td>
              <td class="py-3 px-3 text-center">
                <span class="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  {{ s.conversations_opened }}
                </span>
              </td>
              <td class="py-3 px-3 text-center">
                <span class="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {{ s.conversations_resolved }}
                </span>
              </td>
              <td class="py-3 px-3 text-center">
                <span class="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold bg-n-brand/10 text-n-brand">
                  {{ s.total_actions }}
                </span>
              </td>
              <td class="py-3 px-3 text-center">
                <span class="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  {{ formatDuration(s.avg_online_minutes) }}
                </span>
              </td>
              <td class="py-3 px-3 text-center">
                <span class="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                  {{ formatDuration(s.avg_offline_minutes) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template v-else-if="activeTab === 'logs'">
      <div v-if="logs.length === 0" class="flex flex-col items-center justify-center py-20 text-center">
        <span class="i-lucide-scroll-text size-16 text-n-slate-8 mb-4" />
        <h3 class="text-lg font-semibold text-n-slate-12 mb-1">Nenhum registro encontrado</h3>
        <p class="text-sm text-n-slate-11">Selecione outro período ou agente.</p>
      </div>

      <template v-else>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-n-weak">
                <th class="text-left py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Data / Hora</th>
                <th class="text-left py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Agente</th>
                <th class="text-center py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Evento</th>
                <th class="text-center py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Conversa</th>
                <th class="text-left py-3 px-3 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">IP</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs" :key="log.id" class="border-b border-n-weak/50 hover:bg-n-alpha-1 transition-colors">
                <td class="py-2.5 px-3 text-xs text-n-slate-12 font-mono">{{ formatDateTime(log.created_at) }}</td>
                <td class="py-2.5 px-3">
                  <span class="text-sm text-n-slate-12">{{ log.user_name }}</span>
                </td>
                <td class="py-2.5 px-3 text-center">
                  <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium" :class="eventColor(log.event_type)">
                    <span :class="eventIcon(log.event_type)" class="size-3" />
                    {{ eventLabel(log.event_type) }}
                  </span>
                </td>
                <td class="py-2.5 px-3 text-xs text-n-slate-11 font-mono">
                  <span v-if="log.conversation_id">#{{ log.conversation_id }}</span>
                  <span v-else>—</span>
                </td>
                <td class="py-2.5 px-3 text-xs text-n-slate-10 font-mono">{{ log.ip_address || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex items-center justify-between mt-4 pt-4 border-t border-n-weak">
          <span class="text-xs text-n-slate-10">
            Mostrando {{ ((logsMeta.page - 1) * logsMeta.per_page) + 1 }}–{{ Math.min(logsMeta.page * logsMeta.per_page, logsMeta.total) }} de {{ logsMeta.total }}
          </span>
          <div class="flex gap-2">
            <button
              :disabled="logsMeta.page <= 1"
              class="px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2 transition-colors disabled:opacity-40"
              @click="prevPage"
            >
              Anterior
            </button>
            <button
              :disabled="logsMeta.page * logsMeta.per_page >= logsMeta.total"
              class="px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2 transition-colors disabled:opacity-40"
              @click="nextPage"
            >
              Próximo
            </button>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>
